// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useMemo, useEffect, useCallback } from 'react';

import { type WorkOrder, type WorkOrderItem, useEvolvePaginatedGet, type TaskPaginated } from '@evolvemep/api-ts';
import { Flex, Stack, Text } from '@mantine/core';

import { TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { AddTasksMenu } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/AddTasksMenu';
import { DeleteTaskModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/DeleteTaskModal';
import { EditableTask } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/EditableTask';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';

type Props = {
  workOrder: WorkOrder;
  workOrderItem: WorkOrderItem;
  onTasksUpdated: () => void;
  onTaskAttachmentsChanged?: () => void;
};

export const WorkOrderItemTaskList = ({
  workOrder,
  workOrderItem,
  onTasksUpdated: onTasksUpdatedParent,
  onTaskAttachmentsChanged,
}: Props) => {
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);
  const [searchPhrase, setSearchPhrase] = useState('');
  const defaultOpts = useMemo(
    () => ({
      defaultConfig: {
        params: {
          workOrderItemId: workOrderItem.workOrderItemId,
          taskName: encodeURIComponent(searchPhrase),
        },
      },
    }),
    [searchPhrase, workOrderItem.workOrderItemId],
  );
  const {
    data,
    fetchNextPage,
    setDefaultOpts,
    loading,
    entireCount,
    reset: refresh,
  } = useEvolvePaginatedGet('shop/task', defaultOpts);
  useEffect(() => setDefaultOpts(defaultOpts), [setDefaultOpts, defaultOpts]);

  const onTasksUpdated = useCallback(() => {
    refresh();
    onTasksUpdatedParent();
  }, [refresh, onTasksUpdatedParent]);

  const canEditTasks = canEditWorkOrder('editTasks');

  const [deletingTask, setDeletingTask] = useState<TaskPaginated | null>(null);

  return (
    <>
      <Stack flex={1} style={{ overflow: 'hidden' }}>
        <Flex align="center" justify="space-between" gap="xs">
          <TextInputDebounced onChange={setSearchPhrase} placeholder="Search..." w={300} />
          {canEditTasks && (
            <AddTasksMenu
              workOrder={workOrder}
              parentWorkOrderItem={workOrderItem}
              onTasksUpdated={onTasksUpdated}
              size="sm"
            />
          )}
        </Flex>
        <Stack flex={1} style={{ overflowY: 'scroll' }}>
          <Stack flex="1 0 auto" pr="xs">
            {data.map((task) => (
              <EditableTask
                key={task.taskId}
                task={task}
                workOrder={workOrder}
                onTaskUpdated={(updatedTask) => {
                  // Only refresh the list and parent
                  // if it's possible that the task updates changed the list order
                  // or the parent status
                  if (
                    updatedTask.taskName !== task.taskName ||
                    updatedTask.taskStatusTypeName !== task.taskStatusTypeName
                  ) {
                    onTasksUpdated();
                  }
                  // If the task's attachment count changed, the work order's rollup attachment list is stale
                  if (updatedTask.numberOfAttachments !== task.numberOfAttachments) {
                    onTaskAttachmentsChanged?.();
                  }
                }}
                onDeleteTask={() => setDeletingTask(task)}
              />
            ))}
            {entireCount === 0 && (
              <Stack justify="center" align="center" flex={1} c="dimmed" fz="sm" gap="xs">
                {searchPhrase ? (
                  <>
                    <Text>No tasks found for this item.</Text>
                    <Text>At least one task must be added to every item to release a work order.</Text>
                  </>
                ) : (
                  <Text>No results found.</Text>
                )}
              </Stack>
            )}
            <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
          </Stack>
        </Stack>
      </Stack>

      <DeleteTaskModal
        task={deletingTask}
        onTaskDeleted={() => {
          onTasksUpdated();
          // Deleting a task with attachments also removes them from the work order's rollup attachment list
          if ((deletingTask?.numberOfAttachments ?? 0) > 0) {
            onTaskAttachmentsChanged?.();
          }
        }}
        onClose={() => setDeletingTask(null)}
      />
    </>
  );
};
